import { useState, useEffect, useMemo } from "react";

import ApiService from "../../../../services/apiService";

import CollapseControl from "../../../../components/CollapseControl";
import CollapseHeader from "../../../../components/CollapseHeader";
import TableStd from "../../../../components/TableStd";
import { PogsignType } from "../../../Plano/type/Pogsign";
import ImgNewBlink from "../../../../assets/images/new_blink.gif";
import secureLocalStorage from "react-secure-storage";
import auth from '../../../../utils/authConstants';

const BoxPlanoAllPogs = () => {
  const [isCollapsed, setIsCollapsed] = useState<Record<string, boolean>>({});
  const [pogsigns, setPogsigns] = useState<Record<string, PogsignType[]>>({});
  const [sortedSigns, setSortedSigns] = useState<string[]>([]);
  const [uniquePogsNonMisc, setUniquePogsNonMisc] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    new ApiService(secureLocalStorage.getItem("access_token"))
      .getAllPlanograms()
      .then((res: any) => {
        setPogsigns(res);
        const collapseState = Object.keys(res).reduce((acc, key) => {
          acc[key] = !res[key].some(pog => pog.newFlag);
          return acc;
        }, {});
        const keys = Object.keys(res).sort();
        const miscIndex = keys.indexOf("Miscellaneous");
        if (miscIndex !== -1) {
          keys.push(keys.splice(miscIndex, 1)[0]);
        }
        setSortedSigns(keys);
        setIsCollapsed(collapseState);
        const uniqueIdsFromNonMisc = new Set();
        Object.values(res).forEach(category => {
          if (category instanceof Array) {
            category.forEach(item => {
              if (item.group !== "Miscellaneous") {
                uniqueIdsFromNonMisc.add(item.link);
              }
            });
          }
        });
        setUniquePogsNonMisc(uniqueIdsFromNonMisc);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const filteredPogsigns = useMemo(() => {
    if (!searchTerm.trim()) return pogsigns;

    const search = searchTerm.toLowerCase();
    const filtered: Record<string, PogsignType[]> = {};

    Object.keys(pogsigns).forEach(key => {
      const categoryMatches = key.toLowerCase().includes(search);

      const matchingPogs = categoryMatches
        ? pogsigns[key]
        : pogsigns[key].filter(pog => {
            const pogId = pog.link?.split('/').pop()?.replace('.pdf', '').toLowerCase() || pog.id.toLowerCase();
            const description = pog.description?.toLowerCase() || '';
            return pogId.includes(search) || description.includes(search);
          });

      if (matchingPogs.length > 0) {
        filtered[key] = matchingPogs;
      }
    });

    return filtered;
  }, [searchTerm, pogsigns]);

  const toggleCollapse = (key: string) => {
    setIsCollapsed({ ...isCollapsed, [key]: !isCollapsed[key] });
  };

  const openAllCollapse = () => {
    setIsCollapsed(Object.keys(isCollapsed).reduce((acc, key) => ({ ...acc, [key]: false }), {}));
  };

  const closeAllCollapse = () => {
    setIsCollapsed(Object.keys(isCollapsed).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
  };

  return (
    <>
      <CollapseControl
        title="IMPORTANT:"
        desc="Printing these POGs in DOC does not replace
            printing POGs from SMS! You MUST print from SMS to clear highlight
            from SMS menu."
        openCollapse={openAllCollapse}
        closeCollapse={closeAllCollapse}
      />

      <div className="mt-6 mb-6 px-2">
        <div className="relative max-w-2xl">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            id="pog-search"
            type="text"
            placeholder="Search by POG ID or Description or Group..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-20 py-2 text-sm border-2 border-gray-200 rounded-lg
                       focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100
                       transition-all duration-200 shadow-sm hover:border-gray-300"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute inset-y-0 right-0 mr-1.5 px-3 py-1 my-1
                         bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300
                         text-gray-700 rounded-md text-xs font-medium
                         transition-all duration-200 shadow-sm hover:shadow"
            >
              Clear
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="mt-2 text-xs text-gray-600 flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>Showing results for <strong className="text-gray-900">"{searchTerm}"</strong></span>
          </div>
        )}
      </div>

      {Object.keys(filteredPogsigns).length === 0 && searchTerm ? (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-6 mb-4 mx-2 shadow-sm">
          <div className="flex items-center justify-center gap-3">
            <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-center font-semibold text-gray-800">
              No planograms found matching <span className="text-yellow-700">"{searchTerm}"</span>
            </p>
          </div>
        </div>
      ) : (
        sortedSigns.filter(key => {
          if (!filteredPogsigns[key]) return false;
          // For Miscellaneous, check if there are any visible POGs after deduplication
          if (key.toUpperCase() === "MISCELLANEOUS") {
            return filteredPogsigns[key].some(pog => !uniquePogsNonMisc.has(pog.link));
          }
          return true;
        }).map((key) => (
        <CollapseHeader
          key={key}
          title={key.toUpperCase()}
          isCollapse={searchTerm.trim() ? false : isCollapsed[key]}
          openCollapse={() => toggleCollapse(key)}
          closeCollapse={() => toggleCollapse(key)}
        >
          <TableStd
            fixedLayout={true}
            titles={[
              { title: "POG ID", align: "left", width: 150 },
              { title: "POG Description & Link", align: "center", width: 400 },
              { title: "Arrival Date", align: "center", width: 150 },
              { title: "Date to be Completed", align: "center", width: 150 },
            ]}
          >
            {filteredPogsigns[key]?.map(pog => (
              (key.toUpperCase() !== "MISCELLANEOUS" || !uniquePogsNonMisc.has(pog.link)) && (
                <tr key={pog.link} className={`text-sm ${pog.newFlag ? 'bg-[#FEE]' : ''}`}>
                  <td className="border-b border-b-[#CCC] px-3 py-[2px]" style={{ width: '150px' }}>
                    <div className="flex items-center gap-2">
                      {pog.newFlag && <img src={ImgNewBlink} alt="" />}
                      <span>{pog.link?.split('/').pop()?.replace('.pdf', '').toUpperCase()}</span>
                    </div>
                  </td>
                  <td align="center" className="border-b border-b-[#CCC] px-3 py-[2px]" style={{ width: '400px' }}>
                    <button
                      onClick={() => {
                        const token = secureLocalStorage.getItem("access_token");
                        fetch(`${auth.REACT_APP_SERVER_API_URL}/get-storage-file?fileName=${pog.link}`, {
                          method: 'GET',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                          },
                        })
                          .then(response => {
                            if (!response.ok) {
                              throw new Error('Network response was not ok');
                            }
                            return response.blob();
                          })
                          .then(blob => {
                            const blobUrl = URL.createObjectURL(blob);
                            window.open(blobUrl, '_blank');
                          })
                          .catch(error => console.error('There was a problem with the fetch operation:', error));
                      }}
                      title="You MUST print this POG from SMS to clear highlight from SMS menu screen."
                      className="text-[#06C] no-underline cursor-pointer"
                    >
                      {pog.description}
                    </button>
                  </td>
                  <td align="center" className="border-b border-b-[#CCC] px-3 py-[2px]" style={{ width: '150px' }}>
                    {pog.arrivalDate}
                  </td>
                  <td align="center" className="border-b border-b-[#CCC] px-3 py-[2px]" style={{ width: '150px' }}>
                    {pog.completionDate}
                  </td>
                </tr>
              )
            ))}
          </TableStd>
        </CollapseHeader>
      )))}
    </>
  );
};

export default BoxPlanoAllPogs;
